import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { topics } from '@/data/videoTopics';
import { 
  Shield, 
  Users, 
  Video, 
  Upload, 
  MessageSquare, 
  TrendingUp,
  Calendar,
  Eye
} from 'lucide-react';

interface DashboardStats {
  totalUsers: number;
  totalDatabaseVideos: number;
  totalHardcodedVideos: number;
  totalComments: number;
  totalForumPosts: number;
  recentUsers: Array<{
    id: string;
    username: string | null;
    email: string | null;
    created_at: string;
  }>;
}

const AdminDashboard = () => {
  const [stats, setStats] = useState<DashboardStats>({
    totalUsers: 0,
    totalDatabaseVideos: 0,
    totalHardcodedVideos: 0,
    totalComments: 0,
    totalForumPosts: 0,
    recentUsers: [],
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      // Fetch total users
      const { count: userCount } = await supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true });

      // Fetch database videos count
      const { count: videoCount } = await supabase
        .from('videos')
        .select('*', { count: 'exact', head: true });

      // Fetch comments count
      const { count: commentCount } = await supabase
        .from('video_comments')
        .select('*', { count: 'exact', head: true });

      // Fetch forum posts count
      const { count: forumCount } = await supabase
        .from('category_forum_posts')
        .select('*', { count: 'exact', head: true });

      // Fetch recent users
      const { data: recentUsers } = await supabase
        .from('profiles')
        .select('id, username, email, created_at')
        .order('created_at', { ascending: false })
        .limit(5);

      // Count hardcoded videos
      const hardcodedCount = topics.reduce((acc, topic) => acc + topic.videos.length, 0);

      setStats({
        totalUsers: userCount || 0,
        totalDatabaseVideos: videoCount || 0,
        totalHardcodedVideos: hardcodedCount,
        totalComments: commentCount || 0,
        totalForumPosts: forumCount || 0,
        recentUsers: recentUsers || [],
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const StatCard = ({ 
    title, 
    value, 
    icon: Icon, 
    description 
  }: { 
    title: string; 
    value: number | string; 
    icon: React.ElementType;
    description?: string;
  }) => (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{loading ? '...' : value}</div>
        {description && (
          <p className="text-xs text-muted-foreground mt-1">{description}</p>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      
      <main className="flex-grow container mx-auto px-4 py-8 pt-24">
        <div className="flex items-center gap-3 mb-8">
          <Shield className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-3xl font-playfair font-bold text-foreground">Admin Dashboard</h1>
            <p className="text-muted-foreground">Manage your platform</p>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold mb-4">Quick Actions</h2>
          <div className="flex flex-wrap gap-4">
            <Button asChild>
              <Link to="/admin/upload-video">
                <Upload className="mr-2 h-4 w-4" />
                Upload Video
              </Link>
            </Button>
            <Button variant="outline" asChild>
              <Link to="/admin/users">
                <Users className="mr-2 h-4 w-4" />
                Manage Users
              </Link>
            </Button>
            <Button variant="outline" asChild>
              <Link to="/admin/videos">
                <Video className="mr-2 h-4 w-4" />
                Manage Videos
              </Link>
            </Button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 mb-8">
          <StatCard
            title="Total Users"
            value={stats.totalUsers}
            icon={Users}
            description="Registered accounts"
          />
          <StatCard
            title="Total Videos"
            value={stats.totalHardcodedVideos + stats.totalDatabaseVideos}
            icon={Video}
            description={`${stats.totalHardcodedVideos} library + ${stats.totalDatabaseVideos} uploaded`}
          />
          <StatCard
            title="Video Comments"
            value={stats.totalComments}
            icon={MessageSquare}
            description="User engagement"
          />
          <StatCard
            title="Forum Posts"
            value={stats.totalForumPosts}
            icon={TrendingUp}
            description="Community discussions"
          />
        </div>

        {/* Recent Activity */}
        <div className="grid gap-8 lg:grid-cols-2">
          {/* Recent Users */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Recent Signups
              </CardTitle>
              <CardDescription>
                Newest members of your community
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <p className="text-muted-foreground">Loading...</p>
              ) : stats.recentUsers.length === 0 ? (
                <p className="text-muted-foreground">No users yet</p>
              ) : (
                <div className="space-y-4">
                  {stats.recentUsers.map((user) => (
                    <div key={user.id} className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">
                          {user.username || 'Anonymous'}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {user.email}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Calendar className="h-3 w-3" />
                        {formatDate(user.created_at)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Platform Overview */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye className="h-5 w-5" />
                Platform Overview
              </CardTitle>
              <CardDescription>
                Quick insights about your platform
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Video Categories</span>
                  <span className="font-medium">{topics.length}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Library Videos</span>
                  <span className="font-medium">{stats.totalHardcodedVideos}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Uploaded Videos</span>
                  <span className="font-medium">{stats.totalDatabaseVideos}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Active Discussions</span>
                  <span className="font-medium">{stats.totalForumPosts}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AdminDashboard;
